import { csvEscape } from "../../helpers/csv";

// Field list for the bronze column metadata template. Order is the import contract.
export const BRONZE_TEMPLATE_FIELDS = [
    { name: "source_entity_column_name", type: "Text", required: "Yes", description: "Name of the column in the entity. Must be unique within the entity.", example: "customer_id" },
    { name: "data_type", type: "Text", required: "Yes", description: "Data type of the column, for example INT, VARCHAR(100) or DATE.", example: "INT" },
    { name: "is_obsolete", type: "Boolean (true/false)", required: "No", description: "Ignored on import; new columns are always imported as active.", example: "false" },
    { name: "logical_name", type: "Text", required: "No", description: "Business-friendly name of the column.", example: "Customer ID" },
    { name: "description", type: "Text", required: "No", description: "What the column holds.", example: "Unique identifier of the customer" },
    { name: "constraint", type: "Text", required: "No", description: "Constraint on the column, for example PRIMARY KEY or NOT NULL.", example: "PRIMARY KEY" },
    { name: "data_format", type: "Text", required: "No", description: "Expected format of the values, for example YYYY-MM-DD.", example: "Integer" },
    { name: "example_values", type: "Text", required: "No", description: "Sample values for the column.", example: "1001" },
    { name: "enable_genai", type: "Boolean (true/false)", required: "No", description: "Whether GenAI can use this column. Must be true or false.", example: "true" },
    { name: "pii", type: "Boolean (true/false)", required: "No", description: "Whether the column holds personal data. Must be true or false.", example: "false" },
    { name: "classification", type: "Text", required: "No", description: "Data classification, for example Public or Confidential.", example: "Internal" },
    { name: "owner", type: "Text", required: "No", description: "Person or team that owns the column.", example: "Sales team" },
    { name: "notes", type: "Text", required: "No", description: "Any other notes.", example: "Loaded nightly" },
] as const;

export const BRONZE_TEMPLATE_HEADERS = BRONZE_TEMPLATE_FIELDS.map((field) => field.name);

// The first row of an exported template binds it to the source entity whose columns it edits.
// It is kept outside the data header so it cannot be mistaken for a source entity column.
export const BRONZE_TEMPLATE_SIGNATURE_HEADER = "__wade_bronze_template_source_entity";

export const buildBronzeTemplateSignatureRow = (sourceEntityId: number, sourceEntityName: string) =>
    [BRONZE_TEMPLATE_SIGNATURE_HEADER, String(sourceEntityId), csvEscape(sourceEntityName.trim())].join(",");

export const readBronzeTemplateSignature = (
    row?: string[] | null,
): { sourceEntityId: number; sourceEntityName: string } | null => {
    if (!row || row[0]?.trim().toLowerCase() !== BRONZE_TEMPLATE_SIGNATURE_HEADER) {
        return null;
    }

    const sourceEntityId = Number(row[1]?.trim());
    const sourceEntityName = row[2]?.trim() ?? "";
    return Number.isSafeInteger(sourceEntityId) && sourceEntityId > 0 && sourceEntityName
        ? { sourceEntityId, sourceEntityName }
        : null;
};

// Entity description for the guide: the stored one, else a sentence from the source and schema.
export const buildEntityDescription = ({
    entityDescription,
    dataSourceName,
    schemaName,
}: {
    entityDescription?: string | null;
    dataSourceName?: string | null;
    schemaName?: string | null;
}) => {
    const stored = entityDescription?.trim();
    if (stored) return stored;
    const source = dataSourceName?.trim();
    const schema = schemaName?.trim();
    if (!source && !schema) return "";
    return `This is a data source entity${source ? ` from data source ${source}` : ""}${schema ? `${source ? " and" : " from"} schema ${schema}` : ""}.`;
};

export const buildBronzeGuideSheets = ({
    entityName,
    entityDescription,
    dataSourceName,
    schemaName,
}: {
    entityName?: string | null;
    entityDescription?: string | null;
    dataSourceName?: string | null;
    schemaName?: string | null;
}) => ({
    entityInfoRows: [
        ["Entity name", entityName?.trim() || ""],
        ["Entity description", buildEntityDescription({ entityDescription, dataSourceName, schemaName })],
    ] as string[][],
    entityFieldRows: BRONZE_TEMPLATE_FIELDS.map((field) => [
        field.name as string,
        field.type as string,
        field.required as string,
        field.description as string,
    ]),
    exampleHeader: BRONZE_TEMPLATE_HEADERS as string[],
    exampleRow: BRONZE_TEMPLATE_FIELDS.map((field) => field.example as string),
});

// importBronzeCsv throws an Error carrying every problem as `errors`.
export const toImportErrorList = (error: any, fallback = "The CSV could not be imported."): string[] => {
    if (Array.isArray(error?.errors) && error.errors.length > 0) {
        return error.errors.map(String);
    }
    return [error?.message || fallback];
};

export const downloadBronzeGuide = async (
    input: {
        entityName?: string | null;
        entityDescription?: string | null;
        dataSourceName?: string | null;
        schemaName?: string | null;
    },
    filename: string,
) => {
    const ExcelJS = await import("exceljs");
    const sheets = buildBronzeGuideSheets(input);
    const workbook = new ExcelJS.Workbook();

    const styleHeader = (row: any) => {
        row.font = { bold: true, color: { argb: "FFFFFFFF" } };
        row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0A2F63" } };
    };

    const entitySheet = workbook.addWorksheet("Entity");
    sheets.entityInfoRows.forEach((cells) => {
        const row = entitySheet.addRow(cells);
        row.getCell(1).font = { bold: true };
    });
    entitySheet.addRow([]);
    styleHeader(entitySheet.addRow(["Column", "Field type", "Required", "Description"]));
    sheets.entityFieldRows.forEach((cells) => entitySheet.addRow(cells));
    entitySheet.addRow([]);
    entitySheet.addRow(["Example (headers and one row of values)"]).font = { bold: true };
    styleHeader(entitySheet.addRow(sheets.exampleHeader));
    entitySheet.addRow(sheets.exampleRow);
    [28, 22, 22, 70, 22, 22, 22, 22, 22, 22, 22, 22, 22].forEach((width, index) => { entitySheet.getColumn(index + 1).width = width; });
    entitySheet.getColumn(2).alignment = { wrapText: true, vertical: "top" };
    entitySheet.getColumn(4).alignment = { wrapText: true, vertical: "top" };

    const exampleSheet = workbook.addWorksheet("Example");
    styleHeader(exampleSheet.addRow(sheets.exampleHeader));
    exampleSheet.addRow(sheets.exampleRow);
    exampleSheet.columns.forEach((column) => { column.width = 26; });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};
